// services/supplierBoxInvoice.service.js
//
// Fetches the invoice attached to a supplier box (box-first design spec
// 2026-07-07, section 8 / invoice-placement design 2026-07-06). Uses
// jsonApiClient ONLY so the Sanctum SPA cookie + X-XSRF-TOKEN + shared
// 401/419 interceptors flow through. Do NOT introduce a new axios instance —
// see the comment block in src/httpConfig/config/axios.js (multiple session
// cookies → "400 Request Header Too Large").
//
// ── WHY boxId, not an invoice id (confirmed against the backend source) ──────
// The box index (`GET /supplier-boxes`) only ever carries `hasInvoice` (bool)
// — never the invoice's own id. `SupplierBoxSchema::fields()`'s `invoice`
// HasOne relation is includable on the SHOW route only ("Includable on SHOW
// only (`?include=invoice`); no related route is registered" — schema
// docblock), and `SupplierInvoice` has no FK linking it to the box's own id
// (it resolves via `collection_plan_id` + `supplier_id`, not `boxId`). So
// there is no `GET /supplier-boxes/{boxId}/invoice` route and no arithmetic
// path from `boxId` to the invoice's id — the box's `?include=invoice` fetch
// is the ONLY way in, and it already returns the full invoice payload
// (invoiceNumber/invoiceDate/totalCents/documentUrl) in one round trip, so a
// second call to `GET /supplier-box-invoices/{id}` is unnecessary. Both the
// list card (only has `hasInvoice`) and the detail page can call this with
// nothing but the boxId they already hold.
import { jsonApiClient } from '@/httpConfig/config/axios.js';

export const SUPPLIER_BOX_INVOICE_FORBIDDEN = 'supplier_box_invoice_forbidden';
export const SUPPLIER_BOX_INVOICE_NOT_FOUND = 'supplier_box_invoice_not_found';

function extractJsonApiDetail(error) {
  return error?.response?.data?.errors?.[0]?.detail || null;
}

function normalizeError(error) {
  const status = error?.response?.status;
  const detail = extractJsonApiDetail(error);
  const normalized = new Error(detail || error?.message || 'Request failed');
  normalized.status = status;
  normalized.detail = detail;
  normalized.original = error;

  if (status === 403) normalized.code = SUPPLIER_BOX_INVOICE_FORBIDDEN;
  else if (status === 404) normalized.code = SUPPLIER_BOX_INVOICE_NOT_FOUND;

  return normalized;
}

/**
 * Fetch the invoice attached to a supplier box, FRESH — never reuse a
 * list-cached copy. `documentUrl` is a 5-minute signed URL (invoice-placement
 * design section 2.1), so this must run at tap-time, not at list-load time.
 *
 * @param {string|number} boxId
 * @returns {Promise<object|null>} the flat (deserialized) invoice resource
 *   (`invoiceNumber`/`invoiceDate`/`totalCents`/`documentUrl`), or `null` when
 *   the box has no invoice relation loaded (defensive — `hasInvoice` said true
 *   but the relation resolved empty).
 */
export async function fetchInvoiceForBox(boxId) {
  try {
    const response = await jsonApiClient.get(`/supplier-boxes/${boxId}`, {
      params: { include: 'invoice' },
    });
    const data = response?.data?.data;
    return data?.invoice ?? null;
  } catch (error) {
    throw normalizeError(error);
  }
}
